import { Theme, css, useTheme } from '@emotion/react';

import { GetServerSidePropsContext, NextPage } from 'next';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';

import { Suspense, SyntheticEvent, useEffect, useMemo, useState } from 'react';
import { useMutation } from 'react-query';

import { useOutlook } from '@hooks/query/useOutlook';
import { captureException } from '@sentry/nextjs';
import { Spacing, Typography } from 'chatie-design-system';

import FloatingCreateButton from '@components/characters/FloatingCreateButton';
import FloatingButton from '@components/common/FloatingButton';
import HomeHeader from '@components/common/HomeHeader';
import { PaginationIterator } from '@components/common/Pagination';
import { useTab } from '@components/common/Tab';
import { PlayHeaderContainer } from '@components/home/HomeHeaderContainer';
import Layout from '@components/layout/Layout';
import { PushSize } from '@components/layout/Push';
import SafeArea from '@components/layout/SafeArea';
import HomeGround from '@components/outlook/HomeGround';
import HomePeek from '@components/outlook/HomePeek';
import OutlookComponent from '@components/outlook/OutlookComponent';
import Frame from '@components/skeleton/Frame';
import PlaySkeleton from '@components/skeleton/PlaySkeleton';
import SkeletonList from '@components/skeleton/SkeletonList';

import { fetchSituations, getPaginationOutlookPage } from '@api/chatbot';
import { postCaptureUUID } from '@api/event';
import {
  IContentGalleryContent,
  IRenderedComponentChips,
} from '@base/outlookModel';
import { DOMAIN, OUTLOOK_PAGE, Platform, QUERY_KEYS } from '@constants';
import { withAuth } from '@contexts/auth';
import { useGlobalContext } from '@contexts/global';
import { safeArea } from '@styles/css';
import { CurationViewType, useAnalytics } from '@utils/analytics';
import { useAdultCertifaction, useMe, useScreen } from '@utils/hook';
import { ParsedStorage } from '@utils/localStorage';
import { setFlow } from '@utils/misc';
import {
  combineProps,
  withDehydrate,
  withGetServerSideProps,
} from '@utils/ssr';
import { useWebView } from '@utils/webview';

type DefaultCharacterTab = 'home';
type HomeGroundTab = 'home_ground';
type HomePeekTab = 'home_peek';

const homeContainerCSS = (isCheming: boolean) => css`
  .container {
    display: flex;
    flex-direction: column;
    gap: ${Spacing.large}px;
    margin-top: ${isCheming ? '10px' : '42px'};
  }
`;

const floatingButtonContainerCSS = (isCheming: boolean) => css`
  max-width: 500px;
  margin: 0 auto;
  position: relative;

  .floating-wrapper {
    display: flex;
    flex-direction: row-reverse;
    gap: 16px;
    width: 100%;
    max-width: 500px;
    padding: 16px;
    position: fixed;
    bottom: ${isCheming ? `${PushSize.bottomTab}px` : '0'};
    z-index: 100;

    .floating-promo {
      position: static;
      display: flex;
      flex: 1;
    }

    .floating-create {
      position: static;
      display: flex;
      align-self: flex-end;
    }
  }
`;

const promoCSS = (theme: Theme) => css`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;

  .upper-container {
    display: flex;
    width: 100%;
    justify-content: space-between;
    align-items: center;

    .title {
      color: ${theme.var.white};
      ${Typography.h5};
    }
    .timer {
      display: inline-flex;
      padding: 4px 6px;
      border-radius: 10px;
      height: 18px;
      background: ${theme.var.gray900};
      color: ${theme.var.white};
      text-align: center;
      font-size: 10px;
      font-style: normal;
      font-weight: 400;
      line-height: 100%;
    }
  }
  .under-container {
    display: flex;
    width: 100%;
    gap: 6px;
    align-items: center;
    color: ${theme.var.white};
    ${Typography.body11}
    margin-top:9px;

    .timer {
      display: flex;
      padding: 4px 6px;
      border-radius: 10px;
      height: 18px;
      background: ${theme.var.gray900};
      color: ${theme.var.white};
      text-align: center;
      font-size: 10px;
      font-style: normal;
      font-weight: 400;
      line-height: 100%;
    }
  }
  .description {
    color: rgba(255, 255, 255, 0.6);
    margin-top: 10px;
    ${Typography.body11}
  }
`;

const PlayDetail = () => {
  const { t } = useTranslation();
  const theme = useTheme();

  const router = useRouter();
  const shareUUID = router.query.share_uid as string;

  const { web } = useWebView();
  const { platform, isCheming } = useGlobalContext();
  const { firebase } = useAnalytics();

  const { data: me, refetch: refetchMe } = useMe({
    suspense: false,
  });
  const { data: isAdult } = useAdultCertifaction();
  const { mutate: postUUID } = useMutation(postCaptureUUID);
  const tabs = useTab<DefaultCharacterTab | HomeGroundTab | HomePeekTab>(
    !isAdult
      ? [
          { label: t('플레이'), value: t('home') },
          { label: t('그라운드'), value: t('home_ground') },
        ]
      : [
          { label: t('플레이'), value: t('home') },
          { label: t('엿보기'), value: t('home_peek') },
          { label: t('그라운드'), value: t('home_ground') },
        ],
    { replace: true, withoutReRendering: true, refresh: false }
  );
  const { activeTab, setLastPeekTabViewDate, lastPeekTabViewDate } = tabs;
  const myMessageToken = me?.chatToken;
  const { usePageOutlook } = useOutlook();
  const outlook = usePageOutlook(
    activeTab,
    {
      suspense: false,
    },
    5
  );
  useScreen('플레이');

  const chips = useMemo(() => {
    return outlook?.data?.pages[0]
      ? (
          outlook?.data?.pages[0]?.items?.find(
            (component) => component?.view_type === 'choice_chips'
          ) as IRenderedComponentChips
        )?.data?.choices
      : [];
  }, [outlook]);

  // const [isAosPurchased, setIsAosPurchased] = useState(false);
  const [promoWidth, setPromoWidth] = useState(0);

  // const isProduction = isEnv('production');
  // const isIos = platform === Platform.ios;

  // const subItemId = SUBSCRIBE_ITEM_ID(isProduction, isIos);
  // const passItemId = PASS_ITEM_ID(isProduction, isIos);

  // const isOverVersion = checkRequiredAppVersion(
  //   platform,
  //   version,
  //   SUBSCIRBE_VERSION
  // );

  const handleMoveCharacterDetail = (id: number) => {
    firebase.logEvent('chatbot_click_play_curation', {
      curation_view_type: 'content_gallery_with_header' as CurationViewType,
    });

    if (platform === Platform.web && id) {
      router.push(`/play/characters/${id}`);
    } else {
      web.openPage(`/play/characters/${id}`);
    }
  };

  const onClickSituation = async (
    e: SyntheticEvent,
    content: IContentGalleryContent
  ) => {
    e.preventDefault();

    firebase.logEvent('chatbot_click_play_curation', {
      curation_view_type: 'content_gallery_with_header' as CurationViewType,
    });

    if (!me) {
      alert('로그인이 필요한 서비스입니다.');

      if (platform === Platform.web) {
        return router.push('/signin');
      }

      return;
    }

    const { id } = content;
    if (platform === Platform.web && id) {
      return router.push(`/play/situations/${id}/chat-room`);
    } else {
      return web.openPage(`/play/situations/${id}/chat-room`);
    }
  };

  const onClickSituationById = async (situationId: number) => {
    if (!me) {
      alert('로그인이 필요한 서비스입니다.');

      if (platform === Platform.web) {
        return router.push('/signin');
      }

      return;
    }

    setTimeout(() => {
      if (platform === Platform.web && situationId) {
        return router.push(`/play/situations/${situationId}/chat-room`);
      } else {
        return web.openPage(`/play/situations/${situationId}/chat-room`);
      }
    }, 300);
    return;
  };

  const handleCreateChatRoom = async (id: number) => {
    const data = await fetchSituations({
      character_id: id,
    });
    if (data && data?.items?.length && data.items[0]?.id) {
      if (platform === Platform.web) {
        router.push(`/play/situations/${data.items[0].id}/chat-room`);
      } else {
        web.openPage(`/play/situations/${data.items[0].id}/chat-room`);
      }
    } else {
      alert('잠시 후 다시 시도해 주세요');
    }
  };

  const handleClickGround = (characterId: number, groundId: number) => {
    if (platform === Platform.web) {
      router.push(`to?url=/play/characters/${characterId}/posts/${groundId}`);
    } else {
      web.openPage(`/to?url=/play/characters/${characterId}/posts/${groundId}`);
    }
  };

  const handleClickGroundCharacter = (characterId: number) => {
    if (platform === Platform.web) {
      router.push(`/to?url=/play/characters/${characterId}?tab=community`);
    } else {
      web.openPage(`/to?url=/play/characters/${characterId}?tab=community`);
    }
  };

  const handleClickChoice = (idx: number, situationId: number) => {
    if (platform === Platform.web) {
      router.push(
        `/play/situations/${situationId}/chat-room?first_message_index=${idx}`
      );
    } else {
      web.openPage(
        `/play/situations/${situationId}/chat-room?first_message_index=${idx}`
      );
    }
  };

  const handleNavigateCreate = () => {
    if (platform === Platform.web) {
      router.push(`/play/characters/create`);
    } else {
      web.openPage(`/play/characters/create`);
    }
  };

  useEffect(() => {
    setFlow({ isDelete: true });
    setFlow({ name: '플레이' });
    firebase.logEvent('select_play', {});

    const date = ParsedStorage.getItem('lastPeekTabViewDate');
    if (date) {
      setLastPeekTabViewDate(date);
    }
  }, []);

  const [remainDate, setRemainDate] = useState<string | null>('');

  useEffect(() => {
    const myMessageToken = me?.chatToken;
    if (window) {
      const promotionWidth = window?.innerWidth - 110;
      setPromoWidth(promotionWidth);
    }

    if (!myMessageToken?.expiredAt) {
      return;
    }
    const timerId = setInterval(() => {
      const now = new Date();
      const targetDate = new Date(myMessageToken?.expiredAt as string);
      const difference = targetDate.getTime() - now.getTime();

      if (difference <= 0) {
        setRemainDate(null);
        clearInterval(timerId);
        refetchMe();
        return;
      }

      const days = Math.floor(difference / (1000 * 60 * 60 * 24));
      const hours =
        Math.floor((difference / (1000 * 60 * 60)) % 24) + days * 24;
      const minutes = Math.floor((difference / (1000 * 60)) % 60);
      const seconds = Math.floor((difference / 1000) % 60);
      const hoursFormatted = hours.toString().padStart(2, '0');
      const minutesFormatted = minutes.toString().padStart(2, '0');
      const secondsFormatted = seconds.toString().padStart(2, '0');

      setRemainDate(
        `${hoursFormatted}:${minutesFormatted}:${secondsFormatted}`
      );
    }, 1000);
  }, [me]);

  // const handleClickPromotion = () => {
  //   if (!isOverVersion) {
  //     const text = '앱을 최신버전으로 업데이트 해주세요!';
  //     return alert(text);
  //   }
  //   if (platform === Platform.web) {
  //     router.push('/payment?tab=subscription');
  //   }
  //   web.openPage('/payment?tab=subscription');
  // };

  // const checkPurchase = async () => {
  //   try {
  //     await fetchSubscriptionDetail(subItemId);
  //     await fetchPassItemDetail(passItemId);
  //   } catch (err) {
  //     setIsAosPurchased(true);
  //   }
  // };

  // useEffect(() => {
  //   if (me) {
  //     checkPurchase();
  //   }
  // }, [me, myMessageToken]);

  useEffect(() => {
    if (shareUUID) {
      postUUID(shareUUID);
    }
  }, [shareUUID]);

  const memoizedTabs = useMemo(
    () => tabs,
    [
      tabs.tabs,
      tabs.activeTab,
      tabs.onChange,
      tabs.lastPeekTabViewDate,
      tabs.setLastPeekTabViewDate,
    ]
  );

  return (
    <SafeArea bottom>
      <Suspense
        fallback={<HomeHeader hasNewNotification={false} isHome={true} />}
      >
        <PlayHeaderContainer
          tabs={memoizedTabs}
          lastPeekTabViewDate={lastPeekTabViewDate}
        />
      </Suspense>

      {activeTab === 'home' && (
        <div css={homeContainerCSS(isCheming)}>
          <PaginationIterator
            name="play-outlook-home-list"
            query={outlook}
            className={'container'}
            identifier={'key'}
          >
            {(item, index) => {
              return (
                <Suspense>
                  <OutlookComponent
                    component={item}
                    key={index}
                    order={index}
                    onClickProfile={handleMoveCharacterDetail}
                    onClickSituation={onClickSituation}
                    handleCreateChatRoom={handleCreateChatRoom}
                    onClickSituationById={onClickSituationById}
                    onClickGround={handleClickGround}
                    onClickGroundCharacter={handleClickGroundCharacter}
                    onClickChoice={handleClickChoice}
                  />
                </Suspense>
              );
            }}
          </PaginationIterator>
        </div>
      )}
      {activeTab === 'home_ground' && <HomeGround chips={chips} />}
      {activeTab === 'home_peek' && isAdult && <HomePeek chips={chips} />}
      {!isCheming && activeTab === 'home' && (
        <div css={floatingButtonContainerCSS(isCheming)}>
          <div className="floating-wrapper">
            <FloatingCreateButton handleOnClick={handleNavigateCreate} />

            {/* {!isAosPurchased &&
              isOverVersion &&
              !myMessageToken?.tokenCount &&
              promoWidth && (
                <FloatingButton
                  left={16}
                  backgroundColor={SUBSCRIPTION_GRADIENT_COLOR}
                  borderRadius={10}
                  width={promoWidth}
                  height={68}
                  padding={'12px 10px 12px 14px'}
                  onClick={handleClickPromotion}
                  className={'floating-promo'}
                >
                  <div css={promoCSS}>
                    <div className="upper-container">
                      <p className="title">
                        {t(`할인이 터지는 플레이박스 출시 🎉`)}
                      </p>
                    </div>
                    <div className="under-container">
                      <p>{t('매일 5개씩 대화권을 주는데 3,900원..?')}</p>
                    </div>
                  </div>
                </FloatingButton>
              )} */}
            {!!myMessageToken?.tokenCount && promoWidth && (
              <FloatingButton
                left={16}
                backgroundColor={theme.colors.blue400}
                borderRadius={10}
                width={promoWidth}
                height={62}
                padding={'12px 10px 12px 14px'}
                className={'floating-promo'}
              >
                <div css={promoCSS}>
                  <div className="upper-container">
                    <p className="title">
                      {t(`대화권 ${myMessageToken.tokenCount}장`)}
                    </p>
                    {remainDate && (
                      <div className="timer">
                        {t(`${remainDate} 뒤 소멸 🚨`)}
                      </div>
                    )}
                  </div>
                  <p className="description">
                    {t('모든 대화 모드로 사용할 수 있어요')}
                  </p>
                </div>
              </FloatingButton>
            )}
          </div>
        </div>
      )}
    </SafeArea>
  );
};

const layoutCSS = css`
  ${safeArea('top', 'padding', 0, 68)};
`;

const bannerSkeletonCSS = css`
  padding-bottom: 62.5%;
`;

export const PlayListLoading = () => {
  return (
    <Layout isPush>
      <HomeHeader hasNewNotification={false} />
      <Frame css={bannerSkeletonCSS} />
      <SkeletonList skeleton={PlaySkeleton} />
    </Layout>
  );
};

const Play: NextPage = () => {
  return (
    <Layout css={layoutCSS} isPush>
      {/* <Suspense fallback={<PlayListLoading />}> */}
      <PlayDetail />
      {/* </Suspense> */}
    </Layout>
  );
};

export const getServerSideProps = withGetServerSideProps(
  async (ctx: GetServerSidePropsContext) => {
    return await combineProps(
      ctx,
      withDehydrate(async (queryClient) => {
        try {
          const CHEMING_DOMAIN = process.env.NEXT_PUBLIC_CHEMING_DOMAIN;
          let hostname = '';

          if (ctx.req) {
            hostname = ctx.req.headers.host ?? '';
          } else if (typeof window !== 'undefined') {
            hostname = window.location.host;
          }
          const chemingHost = CHEMING_DOMAIN
            ? new URL(CHEMING_DOMAIN).host
            : '';
          const isCheming = hostname === chemingHost;

          const pageName = OUTLOOK_PAGE.home;
          await Promise.all([
            queryClient.prefetchInfiniteQuery(
              QUERY_KEYS.OUT_LOOK.PAGE(pageName),
              async ({ pageParam }) => {
                return await getPaginationOutlookPage(
                  pageName,
                  pageParam,
                  undefined,
                  {
                    'X-SERVICE-ID': isCheming ? DOMAIN.cheming : DOMAIN.chatie,
                  }
                );
              }
            ),
          ]);

          return undefined;
        } catch (e) {
          captureException(e);
          return undefined;
        }
      })
    );
  }
);
export default withAuth(Play, {
  isSignInRequired: false,
  LoadingComponent: PlayListLoading,
});
